
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function SmartDoorbellInstallationPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Smart Doorbell Installation',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional smart doorbell installation in Fresno, CA. Video doorbells with two-way audio, motion detection, and mobile alerts. Ring, Nest, Arlo installation.',
    url: `${siteUrl}/services/smart-home-energy/smart-doorbell-installation`,
  };

  return (
    <>
      <SEOHead
        title="Smart Doorbell Installation Fresno CA | Video Doorbell Ring Nest"
        description="Smart video doorbell installation in Fresno, CA. Ring, Nest, Arlo doorbell installation with two-way audio, motion detection, and mobile alerts. Professional electrician."
        canonical="/services/smart-home-energy/smart-doorbell-installation"
        keywords="smart doorbell installation Fresno, Ring doorbell, Nest doorbell, video doorbell installation"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Smart Doorbell Installation — Fresno, CA"
        heroSubtitle="See and speak to visitors from anywhere with video doorbells"
        heroImage="https://public.readdy.ai/ai/img_res/0cb267d665ccda8d735f1afcf1fdfb66.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Smart%20doorbell%20with%20HD%20camera%20and%20motion%20sensor%20installed%20on%20modern%20front%20door%20with%20smartphone%20showing%20video%20feed%20and%20two-way%20audio%20interface%20contemporary%20home%20exterior%20no%20people%20clean%20design&width=700&height=600&seq=smartdoorbell-fresno-01&orientation=portrait"
        fresnoContent="Fresno homeowners are upgrading to smart video doorbells that provide security, convenience, and package theft protection. See who's at your door from anywhere, speak to visitors through two-way audio, and receive instant alerts when motion is detected. Perfect for Fresno neighborhoods where package theft is a concern, our professional installations ensure your video doorbell works reliably in our hot summers. We serve all Fresno areas including Woodward Park, Fig Garden, Tower District, and surrounding neighborhoods with expert smart doorbell installations that integrate with your existing smart home system."
        features={[
          { icon: 'ri-vidicon-line', title: 'HD Video Recording', description: '1080p or 4K video quality' },
          { icon: 'ri-message-2-line', title: 'Two-Way Audio', description: 'Speak to visitors from your phone' },
          { icon: 'ri-sensor-line', title: 'Motion Detection', description: 'Alerts when someone approaches' },
          { icon: 'ri-notification-line', title: 'Mobile Alerts', description: 'Instant notifications on your phone' },
          { icon: 'ri-cloud-line', title: 'Cloud Recording', description: 'Store video clips for later review' },
          { icon: 'ri-flashlight-line', title: 'Night Vision', description: 'Clear video even in darkness' },
        ]}
        whatsIncluded={[
          'Complete doorbell assessment and recommendation',
          'Professional removal of old doorbell',
          'Smart doorbell installation and mounting',
          'Wiring verification and transformer upgrade if needed',
          'Wi-Fi network connection and optimization',
          'Mobile app setup and user account creation',
          'Motion zone configuration',
          'Cloud storage setup (if applicable)',
          'Integration with existing smart home systems',
          'Complete testing and user training',
        ]}
        faqs={[
          {
            question: "Which smart doorbell is best for Fresno's hot climate?",
            answer:
              "For Fresno's extreme summer heat (100°+ days), we recommend Ring Video Doorbell Pro 2 or Nest Doorbell (wired) as they handle high temperatures better than battery-powered models. Battery doorbells can experience reduced battery life in Fresno's heat. Wired doorbells provide continuous power and more reliable performance. We also recommend models with heat-resistant housings and ensure proper installation with shade consideration to maximize longevity in Central Valley's harsh climate.",
          },
          {
            question: 'Do I need existing doorbell wiring for smart doorbell installation in Fresno?',
            answer:
              "Not necessarily! Battery-powered smart doorbells (like Ring Video Doorbell 4) don't require existing wiring—we just mount them and connect to Wi-Fi. However, wired doorbells provide better performance and don't need battery changes. If you have existing doorbell wiring, we verify it's compatible and upgrade your transformer if needed (most smart doorbells require 16-24V transformers). For Fresno homes without existing wiring, we can run new wiring or recommend battery-powered options.",
          },
          {
            question: 'How much does smart doorbell installation cost in Fresno?',
            answer:
              'Smart doorbell installation in Fresno costs $150-$250 including professional installation if you have existing doorbell wiring. Battery-powered doorbell installation (no wiring needed) costs $75-$125. If transformer upgrade is needed, add $50-$100. The doorbell device itself costs $100-$250 depending on features (Ring, Nest, Arlo). Cloud storage subscriptions (for video recording) typically cost $3-$10/month. The investment provides security, convenience, and package theft protection for your Fresno home.',
          },
          {
            question: "Can smart doorbells record video in Fresno's bright sunlight?",
            answer:
              "Yes! Modern smart doorbells have HDR (High Dynamic Range) technology that handles Fresno's bright sunlight well, preventing washed-out video. We position doorbells to minimize direct sun exposure when possible, and recommend models with adjustable motion zones so you're not getting constant alerts from cars passing on sunny streets. Night vision ensures clear video after dark. All major brands (Ring, Nest, Arlo) perform well in Fresno's lighting conditions with proper installation.",
          },
          {
            question: 'Will my smart doorbell work during Fresno power outages?',
            answer:
              "Battery-powered smart doorbells continue working during power outages since they don't rely on your home’s electricity. Wired doorbells stop working when power is out unless you have battery backup for your Wi-Fi router. For Fresno homes concerned about summer power outages, we recommend battery-powered doorbells or integrating your Wi-Fi system with a UPS (uninterruptible power supply) so your doorbell stays connected even when the grid is down.",
          },
          {
            question: 'How long does smart doorbell installation take in Fresno?',
            answer:
              'Smart doorbell installation takes 30-60 minutes if you have existing doorbell wiring and a compatible transformer. If transformer upgrade is needed, add 30-45 minutes. Battery-powered doorbell installation takes just 15-30 minutes (mounting and app setup). We handle all wiring, mounting, Wi-Fi connection, app configuration, and motion zone setup. We test the doorbell thoroughly and train you on all features before we leave your Fresno home.',
          },
        ]}
        ctaText="Install Your Smart Doorbell"
      />
    </>
  );
}
